import { useTheme } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";
import { useEffectAsync } from "designSystem/hooks";
import { CssGrid, FlexColumn, GridRow } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { useEffect, useRef } from "react";
import { useConversationHistoryStyles } from "src/Chat/component/styles";
import LoadingMSTeams from "./LoadingMSTeams";
import Message from "./Message";
import MessageGroup from "./MessageGroup";
import { useMSTeamsStore } from "./MSTeamsStore";

const SMALLEST_MESSAGE_HEIGHT = 45.2; // this is the smallest a message's height can be

const pageSize = (height: number): string => {
	return Math.min(50, Math.floor(height / SMALLEST_MESSAGE_HEIGHT))
		.toString();
};

const ConversationHistory = observer(() => {
	const {
		fetchMessages,
		messageGroups,
		moreMessages,
		readyToReadChannelHistory,
	} = useMSTeamsStore();

	const { messageSkeleton } = useConversationHistoryStyles(useTheme());

	const ref = useRef();

	const handleLoadMore = async () => {
		const box = (ref.current as HTMLElement);
		const prevHeight = box.scrollHeight;
		await fetchMessages(pageSize(box.clientHeight));
		box.scrollTop = box.clientHeight - prevHeight;
	};

	useEffectAsync(async () => {
		await fetchMessages();
	}, []);

	const skeleton = (
		<GridRow
			growChild={1}
			item
			key="skeleton_key"
			sx={messageSkeleton}
		>
			<Skeleton
				height={32}
				variant="circular"
				width={32}
			/>
			<Skeleton
				height={32}
				variant="rectangular"
			/>
		</GridRow>
	);

	const children = moreMessages ? [skeleton] : [];

	(messageGroups.forEach((group) =>
		children.push(<MessageGroup
			group={group}
			key={group.dateAsString}
		              />)));

	return (
		<>
			{!readyToReadChannelHistory ?
				<LoadingMSTeams />
				: (
					<div
						ref={ref}
						style={{
							display: "flex",
							flexDirection: "column-reverse",
							flexGrow: 1,
							overflowX: "hidden",
							overflowY: "scroll",
						}}
					>
						<CssGrid
							catchAllAppearances
							columnsByWidth={[[1, 1]]}
							hasMoreResults={moreMessages}
							infiniteScrolling
							onMoreResults={handleLoadMore}
							scrollDirectionIsUp
						>
							{children}
						</CssGrid>
					</div>
				)}
		</>
	);
});

export default ConversationHistory;